import { useState } from "react";

/** How much of a kind of mark the timeline shows. */
export interface TimelineMarks {
  /** Every kill, those made by our players, or none. */
  kills: "all" | "ours" | "none";
  /** The plant. */
  bomb: "show" | "hide";
  /** A mark per grenade thrown: all, ours, or none (the default: they are many). */
  utility: "all" | "ours" | "none";
  /** Notes: every note, the viewer's own, or none. */
  notes: "all" | "mine" | "none";
}

/** How the demo viewer plays, kept in this browser. */
export interface ViewerSettings {
  /** At the end of a round, carry on into the next one. */
  autoNext: boolean;
  /** Start each round when it goes live, freeze time skipped (a second before). */
  skipFreeze: boolean;
  /** The map follows the players still alive, zoomed on them. */
  autoZoom: boolean;
  /** Each player's name beside their weapon on the map. */
  names: boolean;
  /** What the timeline marks (0.26.0). */
  timeline: TimelineMarks;
}

export const TIMELINE_CHOICES: { [K in keyof TimelineMarks]: readonly TimelineMarks[K][] } = {
  kills: ["all", "ours", "none"],
  bomb: ["show", "hide"],
  utility: ["all", "ours", "none"],
  notes: ["all", "mine", "none"],
};
export const DEFAULT_TIMELINE: TimelineMarks = { kills: "all", bomb: "show", utility: "none", notes: "all" };
export const DEFAULT_VIEWER_SETTINGS: ViewerSettings = { autoNext: true, skipFreeze: false, autoZoom: false, names: true, timeline: DEFAULT_TIMELINE };
const KEY = "69ly.viewerSettings";

/** Settings as saved, kept only where they are still valid (a browser may hold an older shape). */
export function readSettings(saved: unknown): ViewerSettings {
  const out: ViewerSettings = { ...DEFAULT_VIEWER_SETTINGS, timeline: { ...DEFAULT_TIMELINE } };
  if (!saved || typeof saved !== "object") return out;
  const s = saved as Record<string, unknown>;
  for (const k of ["autoNext", "skipFreeze", "autoZoom", "names"] as const) if (typeof s[k] === "boolean") out[k] = s[k];
  const tl = s.timeline as Record<string, unknown> | undefined;
  if (tl && typeof tl === "object")
    for (const k of Object.keys(TIMELINE_CHOICES) as (keyof TimelineMarks)[]) {
      const v = tl[k];
      if ((TIMELINE_CHOICES[k] as readonly unknown[]).includes(v)) (out.timeline as unknown as Record<string, unknown>)[k] = v;
    }
  return out;
}

export function useViewerSettings(): [ViewerSettings, (change: Partial<ViewerSettings>) => void] {
  const [settings, setSettings] = useState<ViewerSettings>(() => {
    try {
      return readSettings(JSON.parse(localStorage.getItem(KEY) ?? "{}"));
    } catch {
      return DEFAULT_VIEWER_SETTINGS;
    }
  });
  const change = (c: Partial<ViewerSettings>) =>
    setSettings((s) => {
      const next = { ...s, ...c };
      try {
        localStorage.setItem(KEY, JSON.stringify(next));
      } catch {
        // Private window: kept for this visit.
      }
      return next;
    });
  return [settings, change];
}
